import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface FocusBlock {
  id: string;
  title: string;
  block_date: string;
  start_time: string;
  end_time: string;
  is_recurring: boolean;
  recurring_days: number[];
  color: string;
  is_active: boolean;
}

interface FocusTimeProps {
  selectedDate: string;
}

const FocusTime: React.FC<FocusTimeProps> = ({ selectedDate }) => {
  const [focusBlocks, setFocusBlocks] = useState<FocusBlock[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    title: 'Focus Time',
    blockDate: selectedDate,
    startTime: '09:00',
    endTime: '11:00',
    isRecurring: false,
    recurringDays: [] as number[],
    color: '#ef4444'
  });

  const fetchFocusBlocks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getFocusBlocks(selectedDate);
      if (response.success) {
        setFocusBlocks(response.focusBlocks || []);
      }
    } catch (error) {
      console.error('Failed to fetch focus blocks:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchFocusBlocks();
    setFormData(prev => ({ ...prev, blockDate: selectedDate }));
  }, [selectedDate, fetchFocusBlocks]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiService.createFocusBlock(formData);
      setShowAddModal(false);
      setFormData({
        title: 'Focus Time',
        blockDate: selectedDate,
        startTime: '09:00',
        endTime: '11:00',
        isRecurring: false,
        recurringDays: [],
        color: '#ef4444'
      });
      fetchFocusBlocks();
    } catch (error) {
      console.error('Failed to create focus block:', error);
      alert('Failed to create focus block');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this focus block?')) return;
    try {
      await apiService.deleteFocusBlock(id);
      fetchFocusBlocks();
    } catch (error) {
      console.error('Failed to delete focus block:', error);
    }
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes} ${period}`;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{ padding: '24px' }} data-testid="focus-time">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Focus Time Protector</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Block distraction-free deep work periods</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          data-testid="add-focus-btn"
          style={{
            padding: '10px 20px',
            backgroundColor: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer'
          }}
        >
          + Add Focus Block
        </button>
      </div>

      {/* Info Banner */}
      <div style={{
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
      }}>
        <p style={{ margin: 0, color: '#991b1b', fontSize: '0.9rem' }}>
          <strong>How it works:</strong> Focus blocks will prevent scheduling tasks during these times and remind you to stay focused.
        </p>
      </div>

      {/* Focus Blocks List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : focusBlocks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>No focus blocks for this day</p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '12px 24px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            Create Your First Focus Block
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {focusBlocks.map(block => (
            <div
              key={block.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: 'white',
                borderRadius: '12px',
                border: `2px solid ${block.color}`,
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{
                width: '8px',
                height: '50px',
                backgroundColor: block.color,
                borderRadius: '4px',
                marginRight: '16px'
              }} />
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>{block.title}</h3>
                <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: '#6b7280' }}>
                  {formatTime(block.start_time)} - {formatTime(block.end_time)}
                </p>
                {block.is_recurring && block.recurring_days && (
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                    Repeats: {block.recurring_days.map(d => dayNames[d]).join(', ')}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDelete(block.id)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#fee2e2',
                  color: '#dc2626',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '16px', padding: '24px',
            width: '90%', maxWidth: '450px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '1.25rem', fontWeight: 700 }}>Add Focus Block</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#374151' }}>Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#374151' }}>Start Time</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#374151' }}>End Time</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    style={{ width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, color: '#374151' }}>Color</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'].map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      style={{
                        width: '36px', height: '36px',
                        backgroundColor: color,
                        border: formData.color === color ? '3px solid #1f2937' : 'none',
                        borderRadius: '50%',
                        cursor: 'pointer'
                      }}
                    />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600 }}
                >
                  Create Block
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusTime;
